import { useState } from "react";
import { CheckCircle2, XCircle, Clock, Film } from "lucide-react";
import { saveBlob, shareFiles, zipAll, thumbUrl, resLabel } from "@/lib/reels";
import { ProgressBar } from "@/components/reels/ProgressBar";

const StateIcon = ({ state }) => {
  if (state === "loading") return <span className="rs-spinner" />;
  if (state === "done") return <CheckCircle2 size={16} className="text-[#3c8d4e]" />;
  if (state === "error") return <XCircle size={16} className="text-[#c62d22]" />;
  return <Clock size={16} className="rs-secondary" />;
};

export const Thumb = ({ src, className = "", testId }) => {
  const [broken, setBroken] = useState(false);
  return (
    <div className={`rs-thumb ${className}`} data-testid={testId}>
      {src && !broken ? <img src={thumbUrl(src)} alt="" loading="lazy" onError={() => setBroken(true)} /> : <Film size={18} className="rs-secondary" />}
    </div>
  );
};

const Row = ({ item, i }) => {
  const caption = item.meta?.caption || item.caption;
  const who = item.meta?.uploader;
  const res = resLabel(item.meta);
  return (
    <li className="flex items-center gap-3 py-2.5" data-testid={`bulk-item-${i}`}>
      <div className="relative shrink-0">
        <Thumb src={item.meta?.thumbnail || item.thumbnail} className="h-[72px] w-[48px]" testId={`bulk-item-${i}-thumb`} />
        <span className="absolute -bottom-1 -right-1 rounded-full bg-white p-0.5"><StateIcon state={item.state} /></span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-[14px] leading-snug" data-testid={`bulk-item-${i}-caption`}>
          {caption || item.url.replace(/^https?:\/\/(www\.)?/, "")}
        </p>
        <p className="rs-secondary mt-0.5 flex items-center gap-1.5 text-[12px]">
          {who && <span className="truncate">@{who}</span>}
          {res && <span className="rs-badge" data-testid={`bulk-item-${i}-res`}>{res}</span>}
        </p>
        {item.state === "loading" && <ProgressBar progress={item.progress} compact testId={`bulk-item-${i}-progress`} />}
        {item.error && <p className="text-[12px] text-[#c62d22]" data-testid={`bulk-item-${i}-error`}>{item.error}</p>}
      </div>
      {item.state === "done" && (
        <button className="rs-bordered text-[13px]" data-testid={`bulk-item-${i}-save-btn`} onClick={() => saveBlob(item.blob, item.name)}>Save</button>
      )}
    </li>
  );
};

export const BulkResults = ({ items, busy }) => {
  const [msg, setMsg] = useState("");
  const done = items.filter((i) => i.state === "done");
  const failed = items.filter((i) => i.state === "error").length;

  const share = async () => {
    const ok = await shareFiles(done).catch(() => false);
    if (!ok) setMsg("Sharing multiple files isn't supported here. Use Download ZIP.");
  };

  return (
    <div className="rs-material p-4 rs-fade" data-testid="bulk-results">
      <p className="rs-secondary mb-1 text-[13px]" data-testid="bulk-summary">
        {done.length} of {items.length} saved{failed ? ` · ${failed} failed` : ""}
      </p>
      <ul className="divide-y divide-[#eadfcb]">{items.map((it, i) => <Row key={`${it.url}-${i}`} item={it} i={i} />)}</ul>
      {!busy && done.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          <button className="rs-bordered" data-testid="bulk-zip-btn" onClick={() => zipAll(done)}>Download ZIP</button>
          <button className="rs-bordered" data-testid="bulk-save-all-btn"
            onClick={() => done.forEach((it, k) => setTimeout(() => saveBlob(it.blob, it.name), k * 400))}>Save All</button>
          <button className="rs-bordered" data-testid="bulk-share-btn" onClick={share}>Share All</button>
        </div>
      )}
      {msg && <p className="rs-secondary mt-2 text-center text-[13px]" data-testid="bulk-share-msg">{msg}</p>}
    </div>
  );
};
